<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/LabelActions.vue';
import CustomAttributeActions from 'dashboard/components-next/Contacts/ContactsBulkActions/CustomAttributeActions.vue';
import ContactAgentSelector from './ContactAgentSelector.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'addCustomAttributes',
  'assignAgent',
  'toggleAll',
  'deleteSelected',
]);

const { t } = useI18n();

const selectedCount = computed(() => props.selectedContactIds.length);
const totalVisibleContacts = computed(() => props.visibleContactIds.length);
const showLabelSelector = ref(false);
const showCustomAttributeSelector = ref(false);
const showAgentSelector = ref(false);

const selectAllLabel = computed(() => {
  if (!totalVisibleContacts.value) {
    return '';
  }

  return t('CONTACTS_BULK_ACTIONS.SELECT_ALL', {
    count: totalVisibleContacts.value,
  });
});

const selectedCountLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allItems = computed(() =>
  props.visibleContactIds.map(id => ({
    id,
  }))
);

const selectionModel = computed({
  get: () => new Set(props.selectedContactIds),
  set: newSet => {
    if (!props.visibleContactIds.length) {
      emit('toggleAll', false);
      return;
    }

    const shouldSelectAll = props.visibleContactIds.every(id => newSet.has(id));
    emit('toggleAll', shouldSelectAll);
  },
});

const closePopovers = () => {
  showLabelSelector.value = false;
  showCustomAttributeSelector.value = false;
  showAgentSelector.value = false;
};

const emitClearSelection = () => {
  closePopovers();
  emit('clearSelection');
};

const toggleLabelSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  const willOpen = !showLabelSelector.value;
  closePopovers();
  showLabelSelector.value = willOpen;
};

const closeLabelSelector = () => {
  showLabelSelector.value = false;
};

const toggleCustomAttributeSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  const willOpen = !showCustomAttributeSelector.value;
  closePopovers();
  showCustomAttributeSelector.value = willOpen;
};

const closeCustomAttributeSelector = () => {
  showCustomAttributeSelector.value = false;
};

const toggleAgentSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  const willOpen = !showAgentSelector.value;
  closePopovers();
  showAgentSelector.value = willOpen;
};

const closeAgentSelector = () => {
  showAgentSelector.value = false;
};

const handleAssignLabels = labels => {
  emit('assignLabels', labels);
  closeLabelSelector();
};

const handleAddCustomAttributes = attributes => {
  emit('addCustomAttributes', attributes);
  closeCustomAttributeSelector();
};

const handleAssignAgent = agent => {
  emit('assignAgent', agent);
  closeAgentSelector();
};
</script>

<template>
  <div
    class="bulk-action__container sticky top-0 z-10 bg-gradient-to-b from-n-surface-1 from-90% to-transparent pt-1 pb-2"
  >
    <BulkSelectBar
      v-model="selectionModel"
      :all-items="allItems"
      :select-all-label="selectAllLabel"
      :selected-count-label="selectedCountLabel"
      class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
    >
      <template #secondary-actions>
        <Button
          sm
          ghost
          slate
          :label="t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1.5"
          @click="emitClearSelection"
        />
      </template>
      <template #actions>
        <div class="flex items-center gap-2 ml-auto">
          <div
            v-on-click-outside="closeLabelSelector"
            class="relative flex items-center"
          >
            <Button
              sm
              faded
              slate
              icon="i-lucide-tags"
              :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="toggleLabelSelector"
            />
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <LabelActions
                v-if="showLabelSelector"
                class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
                @assign="handleAssignLabels"
              />
            </transition>
          </div>
          <div class="relative flex items-center">
            <Button
              sm
              faded
              slate
              icon="i-lucide-sliders-horizontal"
              :label="t('CONTACTS_BULK_ACTIONS.UPDATE_CUSTOM_ATTRIBUTES')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="toggleCustomAttributeSelector"
            />
            <CustomAttributeActions
              v-if="showCustomAttributeSelector"
              :selected-count="selectedCount"
              @add="handleAddCustomAttributes"
              @close="closeCustomAttributeSelector"
            />
          </div>
          <Policy :permissions="['administrator']">
            <div
              v-on-click-outside="closeAgentSelector"
              class="relative flex items-center"
            >
              <Button
                sm
                faded
                slate
                icon="i-lucide-user-check"
                :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.BUTTON')"
                :disabled="!selectedCount || isLoading"
                :is-loading="isLoading"
                class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
                @click="toggleAgentSelector"
              />
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <ContactAgentSelector
                  v-if="showAgentSelector"
                  :selected-count="selectedCount"
                  @select="handleAssignAgent"
                  @close="closeAgentSelector"
                />
              </transition>
            </div>
          </Policy>
          <Policy :permissions="['administrator']">
            <Button
              v-tooltip.bottom="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              sm
              faded
              ruby
              icon="i-lucide-trash"
              :label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              :aria-label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-1.5 [&>span:nth-child(2)]:hidden"
              @click="emit('deleteSelected')"
            />
          </Policy>
        </div>
      </template>
    </BulkSelectBar>
  </div>
</template>
